import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, MenuItem, Paper, TextField } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
  PHONE_TYPES,
  SHIPPERS,
  SHIPPER_ESN_TYPES,
  SHIPPER_INCLUDE_ORDERS,
  US_STATES,
  shipperUnitSchema,
} from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { api } from '../../lib/api';
import { useCarriers } from '../plans/lookups';
import { BulkScreen } from '../inventory/BulkPage';
import { useProductModelOptions } from '../inventory/api';
import { BULK_CONFIGS } from '../inventory/bulkConfig';
import { withRequiredMessages } from '../inventory/formUtils';
import { productLabel } from '../inventory/labels';

type EsnType = (typeof SHIPPER_ESN_TYPES)[number];

interface UnitForm {
  esnType: string;
  includeOrders: string;
  enrollmentId: string;
  state: string;
  sim: string;
  simModelId: string;
  shipper: string;
  carrierId: string;
  uiccid: string;
  areaCode: string;
  device: string;
  deviceModelId: string;
  phoneType: string;
}

const resolver = withRequiredMessages<UnitForm>(
  zodResolver(shipperUnitSchema) as unknown as Resolver<UnitForm>,
  {
    includeOrders: 'Select Include Orders',
    state: 'State is required',
    sim: 'ESN/SIM is required',
    shipper: 'Select Shipper ID',
    phoneType: 'Select Phone Type',
  },
);

const STATE_OPTIONS = US_STATES.map(([code, name]) => ({
  value: code,
  label: `${code} - ${name}`,
}));

/** "Single Unit": the reference's fields, in its order. */
function SingleUnitForm({ esnType }: { esnType: EsnType }) {
  const notify = useNotify();
  const models = useProductModelOptions();
  const carriers = useCarriers();
  const empty: UnitForm = {
    esnType,
    includeOrders: '',
    enrollmentId: '',
    state: '',
    sim: '',
    simModelId: '',
    shipper: '',
    carrierId: '',
    uiccid: '',
    areaCode: '',
    device: '',
    deviceModelId: '',
    phoneType: '',
  };
  const { control, handleSubmit, reset, setError } = useForm<UnitForm>({
    defaultValues: empty,
    resolver,
  });
  const add = useMutation({
    mutationFn: (input: UnitForm) =>
      api.post<{ message: string }>('/shipments/shipper-unit', input),
  });
  const submit = handleSubmit(async (values) => {
    try {
      const done = await add.mutateAsync({ ...values, esnType });
      notify.success(done.message);
      reset(empty);
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  const modelOptions = (models.data ?? []).map((m) => ({ value: m.id, label: productLabel(m) }));
  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      aria-label="Manage Shipper Inventory: Single Unit"
      onSubmit={(e) => void submit(e)}
      sx={{ p: 2 }}
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
          mb: 2,
        }}
      >
        <TextInput
          control={control}
          name="enrollmentId"
          label="Enrollment ID"
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 25 } }}
        />
        <SelectInput
          control={control}
          name="includeOrders"
          label="Include Orders"
          required
          emptyLabel="Select"
          options={toOptions(SHIPPER_INCLUDE_ORDERS)}
        />
        <SelectInput
          control={control}
          name="state"
          label="State"
          required
          emptyLabel="Select"
          options={STATE_OPTIONS}
        />
        <TextInput
          control={control}
          name="sim"
          label="ESN/SIM"
          required
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 25, inputMode: 'numeric' } }}
        />
        <SelectInput
          control={control}
          name="simModelId"
          label="ESN/SIM Model"
          required
          emptyLabel="Select"
          options={modelOptions}
        />
        <SelectInput
          control={control}
          name="shipper"
          label="Shipper ID"
          required
          emptyLabel="Select"
          options={toOptions(SHIPPERS)}
        />
        <SelectInput
          control={control}
          name="carrierId"
          label="Carrier"
          required
          emptyLabel="Select"
          options={(carriers.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
        <TextInput
          control={control}
          name="uiccid"
          label="UICCID"
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 25 } }}
        />
        <TextInput
          control={control}
          name="areaCode"
          label="Area Code"
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 3, inputMode: 'numeric' } }}
        />
        <TextInput
          control={control}
          name="device"
          label="Device"
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 16, inputMode: 'numeric' } }}
        />
        <SelectInput
          control={control}
          name="deviceModelId"
          label="Device Model"
          emptyLabel="Select"
          options={modelOptions}
        />
        <SelectInput
          control={control}
          name="phoneType"
          label="Phone Type"
          required
          emptyLabel="Select"
          options={toOptions(PHONE_TYPES)}
        />
      </Box>
      <Button type="submit" variant="contained" disabled={add.isPending}>
        Submit
      </Button>
    </Paper>
  );
}

/**
 * Manage Shipper Inventory (Dropship Orders, release switch `shipper-inventory`): "ESN/SIM Type"
 * and "Upload Type" choose the form, as on the reference. Bulk Upload is the bulk tool whose
 * batches make the "Shipper Batch Report".
 */
export function ShipperInventoryPage() {
  const [esnType, setEsnType] = useState<EsnType | ''>('');
  const [upload, setUpload] = useState<'single' | 'bulk' | ''>('');
  return (
    <Box>
      <PageHeader
        title="Manage Shipper Inventory"
        code="PC7"
        actions={
          <Button component={Link} to="/inventory/bulk/shipper-inventory" variant="outlined">
            Shipper Batch Report
          </Button>
        }
      />
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}
        >
          <TextField
            select
            required
            label="ESN/SIM Type"
            value={esnType}
            onChange={(e) => setEsnType(e.target.value as EsnType)}
          >
            <MenuItem value="">
              <em>-- Select --</em>
            </MenuItem>
            {SHIPPER_ESN_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            required
            label="Upload Type"
            value={upload}
            onChange={(e) => setUpload(e.target.value as 'single' | 'bulk')}
          >
            <MenuItem value="">
              <em>-- Select --</em>
            </MenuItem>
            <MenuItem value="single">Single Unit</MenuItem>
            <MenuItem value="bulk">Bulk Upload</MenuItem>
          </TextField>
        </Box>
      </Paper>
      {esnType && upload === 'single' ? <SingleUnitForm key={esnType} esnType={esnType} /> : null}
      {esnType && upload === 'bulk' ? (
        <BulkScreen
          key={esnType}
          embedded
          config={{
            ...BULK_CONFIGS['shipper-inventory'],
            extras: BULK_CONFIGS['shipper-inventory'].extras?.map((x) =>
              x.key === 'esnType' ? { ...x, initial: esnType } : x,
            ),
          }}
        />
      ) : null}
    </Box>
  );
}
